/**
 * Calls.
 *
 * From docs/references/20-calls.png and 21-calls-scrolled.png: a horizontal
 * rail of round actions across the top, then Recent, then the history with a
 * call button on each row.
 *
 * The rail in the reference mixes fixed actions (Call, Schedule, Keypad)
 * with the person's own favourites and groups. Only the fixed three are
 * built: favourites need the contacts service, which has no provider yet,
 * and inventing "Bariola" and "Family" would be the fabricated-contacts
 * mistake a third time.
 *
 * The history is live. There is simply nothing in it yet — neither the mock
 * nor our own server has call data — so it shows an honest empty state
 * rather than a list of invented calls.
 */

import { useEffect, useState } from 'react'
import { calls as callService } from '@zivapp/shared'
import { formatLastSeen, formatDuration } from '@zivapp/shared'
import { useAuth } from '../context/index.js'
import Avatar from '../components/ui/Avatar.jsx'
import './Calls.css'

const ACTIONS = [
  {
    id: 'call',
    label: 'Call',
    icon: 'M6.6 3h3l1.5 4-2.2 1.6a12 12 0 0 0 5.5 5.5L16 11.9l4 1.5v3a2 2 0 0 1-2.2 2A16.5 16.5 0 0 1 4.6 5.2 2 2 0 0 1 6.6 3Z'
  },
  {
    id: 'schedule',
    label: 'Schedule',
    icon: 'M4 6.5h16a1.5 1.5 0 0 1 1.5 1.5v11A1.5 1.5 0 0 1 20 20.5H4A1.5 1.5 0 0 1 2.5 19V8A1.5 1.5 0 0 1 4 6.5ZM7.5 3.5v4M16.5 3.5v4M2.5 11h19'
  },
  { id: 'keypad', label: 'Keypad', icon: null },
  { id: 'favourites', label: 'Favourites', icon: 'M12 20C7 16.2 4 13.2 4 10a3.8 3.8 0 0 1 8-2.6A3.8 3.8 0 0 1 20 10c0 3.2-3 6.2-8 10Z' }
]

/** A keypad is nine dots; a path would be fussier than it is worth. */
function KeypadGlyph () {
  const xs = [7, 12, 17]
  const ys = [6.5, 12, 17.5]
  return (
    <>
      {ys.map(y => xs.map(x => (
        <circle key={`${x}-${y}`} cx={x} cy={y} r="1.6" fill="currentColor" />
      )))}
    </>
  )
}

/**
 * The arrow that says what kind of call it was. Direction and outcome in one
 * mark, which is the only thing a history row really has to communicate.
 */
function Direction ({ outgoing, missed }) {
  return (
    <svg
      className={`ca__arrow${missed ? ' is-missed' : ''}`}
      viewBox="0 0 24 24"
      fill="none"
      aria-label={missed ? 'Missed' : outgoing ? 'Outgoing' : 'Incoming'}
    >
      {outgoing
        ? <path d="M7 17 17 7m0 0H9m8 0v8" stroke="currentColor" strokeWidth="2.2"
            strokeLinecap="round" strokeLinejoin="round" />
        : <path d="M17 7 7 17m0 0h8m-8 0V9" stroke="currentColor" strokeWidth="2.2"
            strokeLinecap="round" strokeLinejoin="round" />}
    </svg>
  )
}

export default function Calls () {
  const { user } = useAuth()
  const [history, setHistory] = useState([])
  const [isLoading, setLoading] = useState(true)

  useEffect(() => callService.subscribeToHistory(rows => {
    setHistory(rows)
    setLoading(false)
  }), [])

  return (
    <div className="ca">
      <header className="ca__bar">
        <h1 className="ca__title">Calls</h1>
        <div className="ca__bar-actions">
          <button className="ca__icon-btn" type="button" aria-label="Search">
            <svg viewBox="0 0 24 24" fill="none">
              <circle cx="11" cy="11" r="7" stroke="currentColor" strokeWidth="1.9" />
              <path d="m16.5 16.5 4 4" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" />
            </svg>
          </button>
          <button className="ca__icon-btn" type="button" aria-label="Menu">
            <svg viewBox="0 0 24 24" fill="none">
              <circle cx="12" cy="5" r="1.7" fill="currentColor" />
              <circle cx="12" cy="12" r="1.7" fill="currentColor" />
              <circle cx="12" cy="19" r="1.7" fill="currentColor" />
            </svg>
          </button>
        </div>
      </header>

      <div className="ca__rail">
        {ACTIONS.map(action => (
          <button key={action.id} type="button" className="ca__action">
            <span className="ca__action-disc">
              <svg viewBox="0 0 24 24" fill="none">
                {action.id === 'keypad'
                  ? <KeypadGlyph />
                  : <path d={action.icon} stroke="currentColor" strokeWidth="1.8"
                      strokeLinecap="round" strokeLinejoin="round" />}
              </svg>
            </span>
            <span className="ca__action-label">{action.label}</span>
          </button>
        ))}
      </div>

      <div className="ca__scroll">
        <h2 className="ca__heading">Recent</h2>

        {isLoading ? (
          <p className="ca__state">Loading…</p>
        ) : history.length === 0 ? (
          <div className="ca__empty">
            <span className="ca__empty-art" aria-hidden="true">
              <svg viewBox="0 0 24 24" fill="none">
                <path d="M6.6 3h3l1.5 4-2.2 1.6a12 12 0 0 0 5.5 5.5L16 11.9l4 1.5v3a2 2 0 0 1-2.2 2A16.5 16.5 0 0 1 4.6 5.2 2 2 0 0 1 6.6 3Z"
                  stroke="currentColor" strokeWidth="1.4" strokeLinejoin="round" />
              </svg>
            </span>
            <p className="ca__empty-title">No calls yet</p>
            <p className="ca__empty-text">
              Calls you make and receive will appear here.
            </p>
          </div>
        ) : (
          <ul className="ca__list">
            {history.map(call => {
              const outgoing = call.callerId === user?.id
              const missed = call.status === 'missed' || call.status === 'declined'
              return (
                <li key={call.id}>
                  <div className="ca__row">
                    <Avatar name={call.peerName ?? call.callerId} size={46} />

                    <span className="ca__body">
                      <span className={`ca__name${missed ? ' is-missed' : ''}`}>
                        {call.peerName ?? call.callerId}
                      </span>
                      <span className="ca__when">
                        <Direction outgoing={outgoing} missed={missed} />
                        {formatLastSeen(call.createdAt)}
                        {call.durationMs > 0 && ` · ${formatDuration(call.durationMs)}`}
                      </span>
                    </span>

                    <button className="ca__dial" type="button"
                      aria-label={`Call ${call.peerName ?? 'back'}`}>
                      <svg viewBox="0 0 24 24" fill="none">
                        <path d="M6.6 3h3l1.5 4-2.2 1.6a12 12 0 0 0 5.5 5.5L16 11.9l4 1.5v3a2 2 0 0 1-2.2 2A16.5 16.5 0 0 1 4.6 5.2 2 2 0 0 1 6.6 3Z"
                          stroke="currentColor" strokeWidth="1.8" strokeLinejoin="round" />
                      </svg>
                    </button>
                  </div>
                </li>
              )
            })}
          </ul>
        )}
      </div>

      <button type="button" className="ca__fab" aria-label="New call">
        <svg viewBox="0 0 24 24" fill="none">
          <path d="M6.6 3h3l1.5 4-2.2 1.6a12 12 0 0 0 5.5 5.5L16 11.9l4 1.5v3a2 2 0 0 1-2.2 2A16.5 16.5 0 0 1 4.6 5.2 2 2 0 0 1 6.6 3Z"
            stroke="currentColor" strokeWidth="1.9" strokeLinejoin="round" />
        </svg>
        <span className="ca__fab-plus" aria-hidden="true">
          <svg viewBox="0 0 24 24" fill="none">
            <path d="M12 5v14M5 12h14" stroke="currentColor" strokeWidth="3.4" strokeLinecap="round" />
          </svg>
        </span>
      </button>
    </div>
  )
}
